import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useReports } from '../../hooks/useReports';
import { useAuth } from '../../contexts/SupabaseAuthContext';
import { reportsApi } from '../../services/api';
import { BaseReportTemplate } from '../../components/reports/BaseReportTemplate';
import { transformReportData, validateReportData } from '../../utils/reportDataTransformer';
import { 
  ArrowLeft, 
  Download, 
  Share2, 
  Calendar,
  User,
  Globe,
  Clock,
  Loader,
  AlertCircle,
  RefreshCw,
  FileText
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function ReportViewPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [report, setReport] = useState<any>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch report details and generated data
  useEffect(() => {
    if (!reportId || authLoading || !user) return;

    const fetchReportData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch report details
        const reportResponse = await reportsApi.getReport(reportId);
        setReport(reportResponse.report);

        // Fetch latest generation data if report has been generated
        if (reportResponse.report.report_generations && reportResponse.report.report_generations.length > 0) {
          const latestGeneration = reportResponse.report.report_generations
            .sort((a: any, b: any) => new Date(b.generated_at).getTime() - new Date(a.generated_at).getTime())[0];
          
          if (latestGeneration.status === 'completed' && latestGeneration.data) {
            console.log('Raw report data from API:', latestGeneration.data);
            console.log('Raw data type:', typeof latestGeneration.data);
            console.log('Raw data keys:', latestGeneration.data ? Object.keys(latestGeneration.data) : 'no keys');
            
            // Log detailed structure
            if (latestGeneration.data.summary) {
              console.log('Summary structure:', latestGeneration.data.summary);
            }
            if (latestGeneration.data.trends) {
              console.log('Trends structure:', latestGeneration.data.trends);
            }
            if (latestGeneration.data.insights) {
              console.log('Insights structure:', latestGeneration.data.insights);
            }
            if (latestGeneration.data.customData) {
              console.log('CustomData structure:', latestGeneration.data.customData);
            }
            
            // Transform the data using the new transformer
            const transformedData = transformReportData(
              latestGeneration.data, 
              reportResponse.report.report_type
            );
            
            console.log('Transformed report data:', transformedData);
            
            if (validateReportData(transformedData)) {
              setReportData(transformedData);
            } else {
              console.error('Transformed data failed validation');
              console.log('Validation details:', {
                hasData: !!transformedData,
                hasSummary: !!transformedData?.summary,
                hasMetrics: !!transformedData?.summary?.metrics,
                hasDateRange: !!transformedData?.summary?.dateRange,
                dateRangeStart: transformedData?.summary?.dateRange?.start,
                dateRangeEnd: transformedData?.summary?.dateRange?.end
              });
              setError('Report data is invalid');
            }
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch report data:', err);
        setError(err.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [reportId, authLoading, user]);

  const handleRegenerateReport = async () => {
    if (!reportId) return;
    
    try {
      setIsGenerating(true);
      await reportsApi.generateReport(reportId);
      
      // Refresh the report data after generation
      setTimeout(async () => {
        try {
          const reportResponse = await reportsApi.getReport(reportId);
          setReport(reportResponse.report);
          
          if (reportResponse.report.report_generations && reportResponse.report.report_generations.length > 0) {
            const latestGeneration = reportResponse.report.report_generations
              .sort((a: any, b: any) => new Date(b.generated_at).getTime() - new Date(a.generated_at).getTime())[0];
            
            if (latestGeneration.status === 'completed' && latestGeneration.data) {
              console.log('Raw refreshed report data:', latestGeneration.data);
              
              // Transform the refreshed data
              const transformedData = transformReportData(
                latestGeneration.data, 
                reportResponse.report.report_type
              );
              
              console.log('Transformed refreshed report data:', transformedData);
              
              if (validateReportData(transformedData)) {
                setReportData(transformedData);
              }
            }
          }
        } catch (err) {
          console.error('Failed to refresh report data:', err);
        } finally {
          setIsGenerating(false);
        }
      }, 2000);
    } catch (err: any) {
      console.error('Failed to regenerate report:', err);
      setIsGenerating(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'csv' | 'excel') => {
    if (!reportId) return;
    
    try {
      const response = await reportsApi.exportReport(reportId, format);
      // Handle the export response (e.g., download file)
      console.log(`Exporting report as ${format}`, response);
    } catch (err) {
      console.error(`Failed to export report as ${format}:`, err);
    }
  };

  const handleShare = () => {
    console.log('Sharing report');
    // In real implementation, generate share link
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 mx-auto mb-4 animate-spin" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Report</h2>
          <p className="text-gray-600">Please wait while we fetch your report data...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {error ? 'Error Loading Report' : 'Report not found'}
          </h2>
          <p className="text-gray-600 mb-4">
            {error || "The report you're looking for doesn't exist or has been deleted."}
          </p>
          <Button onClick={() => navigate('/reports')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Reports
          </Button>
        </div>
      </div>
    );
  }

  const hasGeneratedData = reportData && Object.keys(reportData).length > 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/reports')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{report.name}</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300">{report.description}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Badge variant={report.status === 'completed' ? 'default' : 'secondary'}>
                {report.status}
              </Badge>
              
              <Button
                variant="outline"
                onClick={handleRegenerateReport}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Regenerate
              </Button>
              
              {hasGeneratedData && (
                <>
                  <div className="relative">
                    <Button variant="outline" onClick={() => handleExport('pdf')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                  </div>
                  
                  <Button variant="outline" onClick={handleShare}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Report Info */}
        <div className="mb-8">
          <Card className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="flex items-center space-x-3">
                <FileText className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Report Type</p>
                  <p className="font-medium text-gray-900 dark:text-white capitalize">{report.report_type}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <User className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Created by</p>
                  <p className="font-medium text-gray-900 dark:text-white">{report.users?.name || 'Unknown'}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Globe className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Project</p>
                  <p className="font-medium text-gray-900 dark:text-white">{report.projects?.name || 'Unknown'}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Last Updated</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {report.last_generated_at 
                      ? formatDistanceToNow(new Date(report.last_generated_at), { addSuffix: true })
                      : 'Never generated'
                    }
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Debug Info (remove in production) */}
        {process.env.NODE_ENV === 'development' && reportData && (
          <div className="mb-8">
            <Card className="p-4">
              <details>
                <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-2">
                  Debug: Transformed Report Data Structure
                </summary>
                <pre className="text-xs bg-gray-100 p-3 rounded overflow-auto max-h-40">
                  {JSON.stringify(reportData, null, 2)}
                </pre>
              </details>
            </Card>
          </div>
        )}

        {/* Report Display */}
        {hasGeneratedData && reportData ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
            <BaseReportTemplate 
              reportData={reportData}
              reportType={report.report_type}
              reportName={report.name}
              reportDescription={report.description}
            />
          </div>
        ) : (
          /* No Generated Data */
          <Card className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Report Data Available</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
              This report hasn't been generated yet. Click the "Regenerate" button above to generate the report and view your analytics data.
            </p>
            <Button 
              onClick={handleRegenerateReport}
              disabled={isGenerating}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {isGenerating ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
} 